import { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

export const translations = {
  EN: {
    // Navbar
    nav_pricing: "Pricing",
    nav_faq: "FAQ",
    nav_contact: "Contact",
    nav_login: "Log In",
    nav_signup: "Get Started",
    nav_dashboard: "Dashboard",
    nav_account: "Account",
    nav_logout: "Logout",
    
    // Home page
    hero_badge: "AI-Powered Listing Marketing",
    hero_title_1: "AI-powered marketing",
    hero_title_2: "for every listing.",
    hero_subtitle: "MLS remarks, social posts, email copy, and more—instantly. Upload your listing and let AI create your entire marketing package.",
    hero_cta: "Get Started",
    hero_cta_secondary: "See Pricing",
    hero_trial_note: "$20 one-time",
    hero_no_card: "Lifetime access",
    
    video_title: "See ListingPro AI in Action",
    video_subtitle: "Watch how easy it is to create professional marketing content",
    
    stats_listings: "Listings Created",
    stats_time: "Time Saved",
    stats_languages: "Languages",
    
    how_it_works: "How It Works",
    how_it_works_subtitle: "Create professional listing content in three simple steps",
    step1_title: "Add Your Listing",
    step1_desc: "Enter property details and upload photos",
    step2_title: "AI Generates Content",
    step2_desc: "Get MLS remarks, social posts, emails & SMS",
    step3_title: "Copy & Share",
    step3_desc: "One-click copy or share directly",
    
    features_title: "Everything You Need",
    features_subtitle: "Powerful features to market your listings effectively",
    
    cta_title: "Ready to Transform Your Listings?",
    cta_subtitle: "Join thousands of agents using AI to create stunning marketing content.",
    cta_button: "Get Started - $20 One-Time",
    
    // Pricing page
    pricing_badge: "Simple Pricing",
    pricing_title_1: "One price.",
    pricing_title_2: "All features.",
    pricing_subtitle: "Pay once, own forever. No recurring fees.",
    pricing_trial_badge: "Best Value",
    pricing_plan_name: "ListingPro AI",
    pricing_plan_desc: "Everything you need to market your listings",
    pricing_per_month: "one-time",
    pricing_after_trial: "lifetime access",
    pricing_cta: "Get Started",
    pricing_no_card: "Secure payment via Stripe",
    pricing_faq_title: "Have questions?",
    pricing_faq_subtitle: "Check out our FAQ or contact our support team.",
    pricing_view_faq: "View FAQ",
    pricing_contact: "Contact Us",
    
    // Features list
    feature_listings: "Unlimited active listings",
    feature_photo: "AI photo enhancement",
    feature_mls: "MLS remarks generation",
    feature_social: "Social media captions",
    feature_email: "Email marketing copy",
    feature_sms: "SMS marketing copy",
    feature_bilingual: "Bilingual output (EN/ES)",
    feature_copy: "One-click copy for MLS",
    feature_updates: "Pending & Sold updates",
    feature_export: "Export marketing packs",
    feature_pdf: "PDF downloads",
    feature_batch: "Batch photo enhancement",
    feature_share: "Public listing sharing",
    feature_support: "Priority support",
    
    // Footer
    footer_product: "Product",
    footer_company: "Company",
    footer_legal: "Legal",
    footer_about: "About",
    footer_privacy: "Privacy Policy",
    footer_terms: "Terms of Service",
    footer_copyright: "All rights reserved.",
    
    // Auth
    login_title: "Welcome back",
    login_subtitle: "Sign in to your account",
    login_email: "Email",
    login_password: "Password",
    login_forgot: "Forgot password?",
    login_button: "Sign In",
    login_no_account: "Don't have an account?",
    login_signup_link: "Sign up",
    
    signup_title: "Create your account",
    signup_subtitle: "Get started with ListingPro AI",
    signup_name: "Full Name",
    signup_phone: "Phone (optional)",
    signup_email: "Email",
    signup_password: "Password",
    signup_confirm: "Confirm Password",
    signup_language: "Preferred Language",
    signup_trial_note: "Start with 1 free listing. Upgrade to Pro for unlimited access.",
    signup_button: "Create Account",
    signup_have_account: "Already have an account?",
    signup_login_link: "Sign in",
    
    // Language
    language: "Language",
    english: "English",
    spanish: "Español",
    
    // Contact page
    contact_title: "Contact Us",
    contact_subtitle: "Have a question or need help? We're here for you.",
    contact_email: "Email",
    contact_email_value: "erick@listingpro-ai.com",
    contact_live_chat: "Live Chat",
    contact_live_chat_value: "Available Mon-Fri, 9am-5pm EST",
    contact_response_time: "Response Time",
    contact_response_time_value: "Usually within 24 hours",
    contact_form_title: "Send us a message",
    contact_name: "Name",
    contact_name_placeholder: "Your name",
    contact_email_placeholder: "your@email.com",
    contact_message: "Message",
    contact_message_placeholder: "How can we help you?",
    contact_submit: "Send Message",
    contact_sending: "Sending...",
    contact_success: "Message sent! We'll get back to you soon.",
    contact_success_guest: "Thank you for your message! We'll get back to you soon.",
    contact_error: "Failed to send message. Please try again.",
    contact_empty_message: "Please enter a message",
  },
  
  ES: {
    // Navbar
    nav_pricing: "Precios",
    nav_faq: "Preguntas",
    nav_contact: "Contacto",
    nav_login: "Iniciar Sesión",
    nav_signup: "Comenzar",
    nav_dashboard: "Panel",
    nav_account: "Cuenta",
    nav_logout: "Salir",
    
    // Home page
    hero_badge: "Marketing de Listados con IA",
    hero_title_1: "Marketing con IA",
    hero_title_2: "para cada listado.",
    hero_subtitle: "Descripciones MLS, publicaciones sociales, correos y más—al instante. Sube tu listado y deja que la IA cree todo tu paquete de marketing.",
    hero_cta: "Comenzar",
    hero_cta_secondary: "Ver Precios",
    hero_trial_note: "$20 pago único",
    hero_no_card: "Acceso de por vida",
    
    video_title: "Mira ListingPro AI en Acción",
    video_subtitle: "Ve lo fácil que es crear contenido de marketing profesional",
    
    stats_listings: "Listados Creados",
    stats_time: "Tiempo Ahorrado",
    stats_languages: "Idiomas",
    
    how_it_works: "Cómo Funciona",
    how_it_works_subtitle: "Crea contenido profesional para tus listados en tres simples pasos",
    step1_title: "Agrega Tu Listado",
    step1_desc: "Ingresa los detalles de la propiedad y sube fotos",
    step2_title: "La IA Genera Contenido",
    step2_desc: "Obtén descripciones MLS, posts sociales, correos y SMS",
    step3_title: "Copia y Comparte",
    step3_desc: "Copia con un clic o comparte directamente",
    
    features_title: "Todo Lo Que Necesitas",
    features_subtitle: "Funciones potentes para comercializar tus listados efectivamente",
    
    cta_title: "¿Listo para Transformar Tus Listados?",
    cta_subtitle: "Únete a miles de agentes que usan IA para crear contenido de marketing impresionante.",
    cta_button: "Comenzar - $20 Pago Único",
    
    // Pricing page
    pricing_badge: "Precio Simple",
    pricing_title_1: "Un precio.",
    pricing_title_2: "Todas las funciones.",
    pricing_subtitle: "Paga una vez, acceso de por vida. Sin pagos recurrentes.",
    pricing_trial_badge: "Mejor Valor",
    pricing_plan_name: "ListingPro AI",
    pricing_plan_desc: "Todo lo que necesitas para comercializar tus listados",
    pricing_per_month: "pago único",
    pricing_after_trial: "acceso de por vida",
    pricing_cta: "Comenzar",
    pricing_no_card: "Pago seguro con Stripe",
    pricing_faq_title: "¿Tienes preguntas?",
    pricing_faq_subtitle: "Consulta nuestras preguntas frecuentes o contacta a soporte.",
    pricing_view_faq: "Ver Preguntas",
    pricing_contact: "Contáctanos",
    
    // Features list
    feature_listings: "Listados activos ilimitados",
    feature_photo: "Mejora de fotos con IA",
    feature_mls: "Generación de descripciones MLS",
    feature_social: "Subtítulos para redes sociales",
    feature_email: "Textos de email marketing",
    feature_sms: "Textos de SMS marketing",
    feature_bilingual: "Salida bilingüe (EN/ES)",
    feature_copy: "Copia con un clic para MLS",
    feature_updates: "Actualizaciones Pendiente y Vendido",
    feature_export: "Exportar paquetes de marketing",
    feature_pdf: "Descargas en PDF",
    feature_batch: "Mejora de fotos en lote",
    feature_share: "Compartir listados públicamente",
    feature_support: "Soporte prioritario",
    
    // Footer
    footer_product: "Producto",
    footer_company: "Compañía",
    footer_legal: "Legal",
    footer_about: "Nosotros",
    footer_privacy: "Política de Privacidad",
    footer_terms: "Términos de Servicio",
    footer_copyright: "Todos los derechos reservados.",
    
    // Auth
    login_title: "Bienvenido de nuevo",
    login_subtitle: "Inicia sesión en tu cuenta",
    login_email: "Correo Electrónico",
    login_password: "Contraseña",
    login_forgot: "¿Olvidaste tu contraseña?",
    login_button: "Iniciar Sesión",
    login_no_account: "¿No tienes cuenta?",
    login_signup_link: "Regístrate",
    
    signup_title: "Crea tu cuenta",
    signup_subtitle: "Comienza con ListingPro AI",
    signup_name: "Nombre Completo",
    signup_phone: "Teléfono (opcional)",
    signup_email: "Correo Electrónico",
    signup_password: "Contraseña",
    signup_confirm: "Confirmar Contraseña",
    signup_language: "Idioma Preferido",
    signup_trial_note: "Comienza con 1 listado gratis. Mejora a Pro para acceso ilimitado.",
    signup_button: "Crear Cuenta",
    signup_have_account: "¿Ya tienes cuenta?",
    signup_login_link: "Inicia sesión",
    
    // Language
    language: "Idioma",
    english: "English",
    spanish: "Español",
    
    // Contact page
    contact_title: "Contáctanos",
    contact_subtitle: "¿Tienes una pregunta o necesitas ayuda? Estamos aquí para ti.",
    contact_email: "Correo Electrónico",
    contact_email_value: "erick@listingpro-ai.com",
    contact_live_chat: "Chat en Vivo",
    contact_live_chat_value: "Disponible Lun-Vie, 9am-5pm EST",
    contact_response_time: "Tiempo de Respuesta",
    contact_response_time_value: "Usualmente dentro de 24 horas",
    contact_form_title: "Envíanos un mensaje",
    contact_name: "Nombre",
    contact_name_placeholder: "Tu nombre",
    contact_email_placeholder: "tu@correo.com",
    contact_message: "Mensaje",
    contact_message_placeholder: "¿Cómo podemos ayudarte?",
    contact_submit: "Enviar Mensaje",
    contact_sending: "Enviando...",
    contact_success: "¡Mensaje enviado! Te responderemos pronto.",
    contact_success_guest: "¡Gracias por tu mensaje! Te responderemos pronto.",
    contact_error: "Error al enviar mensaje. Por favor intenta de nuevo.",
    contact_empty_message: "Por favor ingresa un mensaje",
  }
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    // Check localStorage for saved preference
    const saved = localStorage.getItem('listingpro_language');
    return saved || 'EN';
  });

  useEffect(() => {
    localStorage.setItem('listingpro_language', language);
  }, [language]);

  const t = (key) => {
    return translations[language][key] || translations['EN'][key] || key;
  };

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'EN' ? 'ES' : 'EN');
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
